/**
 * `/shipments` — customer-facing shipments list (Phase 2 Stream C).
 *
 * Migrated from PSM `portal/src/pages/ShipmentsPage.tsx` (~560 LOC). The
 * PSM page rendered a SIG-grouped table (vessel/voyage/B-L + nested
 * vehicles). Phase 2 Stream C ships the **flat shipment list** first; the
 * SIG-grouped view will land separately once the
 * `/shipment-groups` BFF is migrated (the existing projection-reading
 * route is kept for that path until Stream C/F closeout).
 *
 * Strips:
 *   - Tailwind + lucide-react + shadcn (none available yet in customer-portal)
 *   - mobile/desktop fork (Phase 2 uses one responsive layout)
 *   - search / status filter (server doesn't return full status set yet)
 *
 * Spec: docs/specs/PORTAL_INTERNAL_API_CONTRACT.md §2.4 (S1)
 *       docs/specs/PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.2 (Phase 2 Stream C)
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PortalApiError, portalApi } from '../lib/apiClient';
import type { PortalShipment } from '@customer-portal/shared';
import {
  Card,
  EmptyState,
  ErrorState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TablePagination,
  TableRow,
} from '../components/ui';
import { Button } from '../components/ui/Button';

interface ListPayload {
  items: PortalShipment[];
  total: number;
  limit: number;
  offset: number;
}

interface PortalListEnvelope {
  ok: true;
  data: ListPayload;
}

type State =
  | { kind: 'loading' }
  | { kind: 'success'; data: ListPayload }
  | { kind: 'empty' }
  | { kind: 'error'; code: string };

const PAGE_SIZE = 50;

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' });
}

function StatusPill({ status }: { status: string }): JSX.Element {
  const s = status.toLowerCase();
  const palette = (() => {
    if (s.includes('delivered') || s.includes('arrived') || s.includes('完了')) {
      return { bg: '#dcfce7', fg: '#166534' };
    }
    if (s.includes('transit') || s.includes('shipped') || s.includes('輸送')) {
      return { bg: '#dbeafe', fg: '#1d4ed8' };
    }
    if (s.includes('planned') || s.includes('arranged') || s.includes('予定')) {
      return { bg: '#fef3c7', fg: '#92400e' };
    }
    if (s.includes('cancel')) return { bg: '#fee2e2', fg: '#991b1b' };
    return { bg: '#f3f4f6', fg: '#374151' };
  })();
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.125rem 0.5rem',
        borderRadius: '9999px',
        background: palette.bg,
        color: palette.fg,
        fontSize: '0.75rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {status || '—'}
    </span>
  );
}

export function ShipmentsPage(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [offset, setOffset] = useState(0);

  const load = useCallback(async (nextOffset: number, signal: AbortSignal): Promise<void> => {
    setState({ kind: 'loading' });
    try {
      const res = await portalApi<PortalListEnvelope>(
        `/api/portal/shipments?limit=${PAGE_SIZE}&offset=${nextOffset}`,
        { signal },
      );
      const data = res.data;
      if (data.items.length === 0 && nextOffset === 0) {
        setState({ kind: 'empty' });
      } else {
        setState({ kind: 'success', data });
      }
    } catch (err) {
      if (signal.aborted) return;
      if (err instanceof PortalApiError) {
        setState({ kind: 'error', code: err.code });
        return;
      }
      setState({ kind: 'error', code: 'UNKNOWN' });
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(offset, ctrl.signal);
    return () => ctrl.abort();
  }, [load, offset]);

  function handlePrev(): void {
    setOffset((o) => Math.max(0, o - PAGE_SIZE));
  }
  function handleNext(): void {
    if (state.kind === 'success' && state.data.offset + state.data.limit < state.data.total) {
      setOffset((o) => o + PAGE_SIZE);
    }
  }

  return (
    <section data-testid="shipments-page">
      <header style={{ marginBottom: '1rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Shipments</h1>
        <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
          Track your in-transit and arrived shipments.
        </p>
      </header>

      {state.kind === 'loading' ? (
        <div data-testid="shipments-loading">
          <Spinner block label="Loading" />
        </div>
      ) : null}

      {state.kind === 'empty' ? (
        <EmptyState
          title="No shipments yet"
          description="New shipments will appear here once they are registered."
        />
      ) : null}

      {state.kind === 'error' ? (
        <ErrorState
          title="Failed to load the shipment list"
          description={`(${state.code})`}
          onRetry={() => setOffset((o) => o)}
        />
      ) : null}

      {state.kind === 'success' ? (
        <Card aria-label="Shipments list">
          <div data-testid="shipments-table">
            <Table aria-label="Shipments">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Shipment</TableHeaderCell>
                  <TableHeaderCell>Vehicle</TableHeaderCell>
                  <TableHeaderCell>Vessel / Voyage</TableHeaderCell>
                  <TableHeaderCell>Route</TableHeaderCell>
                  <TableHeaderCell>ETD</TableHeaderCell>
                  <TableHeaderCell>ETA</TableHeaderCell>
                  <TableHeaderCell>B/L</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.data.items.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Link
                        to={`/shipments/${s.id}`}
                        data-testid={`shipment-link-${s.id}`}
                        style={{ color: '#1f4ed8', fontWeight: 500, textDecoration: 'none' }}
                      >
                        #{s.id}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                        {s.chassis_number || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {s.vessel_name ? (
                        <>
                          {s.vessel_name}
                          {s.voyage_number ? (
                            <span style={{ color: '#9ca3af', marginLeft: '0.25rem' }}>
                              V.{s.voyage_number}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <span style={{ color: '#374151', fontSize: '0.8125rem' }}>
                        {s.loading_port && s.destination_port
                          ? `${s.loading_port} → ${s.destination_port}`
                          : s.loading_port || s.destination_port || '—'}
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(s.etd)}</TableCell>
                    <TableCell>
                      <span style={{ color: '#1f4ed8' }}>{formatDate(s.eta)}</span>
                    </TableCell>
                    <TableCell>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                        {s.bl_number || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusPill status={s.transport_status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination>
              <span data-testid="shipments-page-info">
                {state.data.offset + 1}–
                {Math.min(state.data.offset + state.data.items.length, state.data.total)} /{' '}
                {state.data.total} shipments
              </span>
              <span style={{ display: 'inline-flex', gap: '0.5rem' }}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handlePrev}
                  disabled={state.data.offset === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleNext}
                  disabled={state.data.offset + state.data.limit >= state.data.total}
                >
                  Next
                </Button>
              </span>
            </TablePagination>
          </div>
        </Card>
      ) : null}
    </section>
  );
}
